import React from "react";
import { store } from "store";
import { useLocation, useNavigate } from "react-router-dom";
//#region types
import { TranslationKeys } from "translations";
import { User } from "models/propTypes/userProfilePropTypes";
import { WintSuperkollAPIModelAdvisor } from "wint-api-client/dist/types";
import { CommentItem } from "models/propTypes/commentViewPropTypes";
import { FileItem } from "models/utilTypes/fileUtilsTypes";
import { DocumentationLogListProps } from "models/propTypes/advisorPropTypes";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import { Grid } from "@mui/material";
import { ActionLinkButton, Comments, Icon } from "components/common";
import CommentsSkeleton from "components/common/skeletons/commentsSkeleton";
import { ShowMoreButton } from "components/common/comments";
import { useDocumentationLogService } from "serviceHooks";
//#endregion
import { FileHelper, LocalizationHelperInstance, UrlHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const DocumentationLogList: React.FC<DocumentationLogListProps> = (props) => {
  const documentationLogService = useDocumentationLogService();
  const location = useLocation();
  const navigate = useNavigate();

  const { widgetView, reloadKey } = props;

  /**
   * @deprecated Direct store.getState() usage is deprecated. Use useSelector hook instead.
   *
   * @see useSelector - React Redux hook for accessing state
   * @see https://app.clickup.com/4523827/v/dc/4a1tk-21632/4a1tk-8592
   *
   * @example
   * // Old way (deprecated):
   * const companyId = store.getState().auth.selectedCompanyId;
   *
   * @example
   * // New way (recommended):
   * const companyId = useSelector((state: AppState) => state.auth.selectedCompanyId);
   */
  const companyId = store.getState().auth.selectedCompanyId;

  const [loading, setLoading] = React.useState<boolean>(true);
  const [documentLogs, setDocumentLogs] = React.useState<
    WintSuperkollAPIModelAdvisor.DocumentationLogItem[] | undefined
  >(undefined);

  React.useEffect(() => {
    if (companyId) {
      setLoading(true);
      documentationLogService
        .getDocumentationLogEntries(companyId)
        .then((res) => {
          setDocumentLogs(res);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [companyId, reloadKey]);

  const getImageContent = (id: number | string) => {
    const logId = documentLogs?.find((l) => l.attachmentReferences?.some((a) => a.fileId === id))?.id;
    return documentationLogService.getAttachment(logId as string, id as string);
  };

  const onFileClick = (id: number | string) => {
    const logId = documentLogs?.find((l) => l.attachmentReferences?.some((a) => a.fileId === id))?.id;
    documentationLogService.downloadAttachment(logId as string, id as string).then((res) => {
      FileHelper.download(res);
    });
  };

  const sortedDocumentLogs = React.useMemo(
    () =>
      documentLogs
        ?.slice()
        .sort((a, b) => new Date(b.created ?? 0).getTime() - new Date(a.created ?? 0).getTime()),
    [documentLogs]
  );

  const onShowMoreClick = () => {
    navigate(UrlHelper.formatPath("/AdvisoryView/DocumentationLog"), {
      state: { background: location },
    });
  };

  const onCustomerCaseClick = (customerCaseId: number) => {
    navigate(UrlHelper.formatPath(`/Support/${customerCaseId}`), {
      state: { background: location },
    });
  };

  return (
    <Grid item container>
      {loading && documentLogs === undefined ? (
        <CommentsSkeleton></CommentsSkeleton>
      ) : (
        <>
          {loading && documentLogs && <CommentsSkeleton commentsCount={1} />}
          <Comments
            parentId={"DocumentationLog"}
            comments={
              // Sort here rather than relying on Comments' own newest-first sort: when
              // showAllComments is unset (our case) Comments pages the raw array it was
              // handed and discards the sorted copy, so the order has to arrive correct.
              sortedDocumentLogs?.map(
                (message, index): CommentItem => ({
                  id: index,
                  message: message.message ?? "",
                  subject: message.subject,
                  date: message.created ?? new Date(),
                  from: new User(message.authorId, message.authorName ?? "", false),
                  attachments: !widgetView
                    ? message.attachmentReferences?.map(
                        (a): FileItem => ({
                          id: a.fileId ?? "",
                          name: a.name,
                          fileMeta: { content: "", contentType: a.contentType },
                        })
                      )
                    : [],
                  footer: message.customerCaseId ? (
                    <ActionLinkButton
                      onClick={onCustomerCaseClick.bind(null, message.customerCaseId)}
                      startIcon={<Icon icon={IconDefinitionConstants.WLink} />}
                      className={cssExports.descriptionLink}
                    >
                      {`${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.SupportCase)}`}
                    </ActionLinkButton>
                  ) : (
                    <></>
                  ),
                })
              ) ?? []
            }
            onFileClick={onFileClick}
            getImageContent={getImageContent}
            viewCountIncreaseButtonText={LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Common.ShowMore
            )}
            viewCountIncreaseBy={10}
            defaultViewCount={3}
            hideViewMore={widgetView}
          />
          {widgetView && documentLogs?.length && documentLogs?.length > 0 ? (
            <Grid item container justifyContent="center">
              <ShowMoreButton onClick={onShowMoreClick} />
            </Grid>
          ) : (
            <></>
          )}
        </>
      )}
    </Grid>
  );
};

export default DocumentationLogList;
